import { useQuery, useMutation } from 'react-query';
import produce from 'immer';
import { useMemo, useCallback } from 'react';
import cloneDeep from 'lodash/cloneDeep';

import { MEDIA_TYPE } from 'components';
import { useDeviceTypeIsMobile } from '@/hooks';
import {
  request,
  queryClient,
  getCurrentAddressInfo,
  getIsOfficialAccountById,
  isSystemAddress,
} from 'utils';

import { useCurrentAddressInfo } from './address';
import { useHiChatLocalUserInfo, useHiUserCache } from './hiChat';

export type AvatarRes = {
  content: string;
  thumbnail_url?: string;
  content_type: MEDIA_TYPE.IMAGE;
};

export type UserDetailRes = {
  id: string;
  account_id?: string;
  comment?: string;
  avatar?: AvatarRes;
  create_at: number;
  follower_count: number;
  following_count: number;
  is_create_user?: boolean;
  is_editor?: boolean;
  is_contract: boolean;
  is_following: boolean;
  is_followed?: boolean;
  is_mine: boolean;
  protocol_usd_value?: number;
  usd_value?: number;
  wallet_usd_value?: number;
  tvf?: number;
  relation?: 'friend' | 'follows me';
  ens?: string;
  email?: string;
  email_verified?: boolean;
  twitter_id?: string;
  twitter_id_verified?: boolean;
  github_id?: string;
  github_id_verified?: boolean;
  is_multisig_addr?: boolean;
  is_mirror_author?: boolean;
  // long -> bullish
  // short -> bearish
  market_status?: 'long' | 'short';
  projects?: {
    chain: string;
    net_usd_value?: number;
    name: string;
    logo_url: string;
  }[];
  used_chains?: Array<string>;
  desc?: UserDesc;
  bio?: string;
};

export const useUserDetail = (id?: string, key?: string) => {
  return useUserInfo(id, 1000, key);
};

export const useUserUsedChains = (id?: string) => {
  const queryKey = ['/user/used_chains', id];

  const query = useQuery(
    queryKey,
    async () => {
      return request.get<{ chains: Array<string> }>('/user/used_chains', { id: id! });
    },
    {
      enabled: !!id,
      cacheTime: 1000,
    },
  );

  return query;
};

export const setUserDetail = ({
  currentId,
  userId,
  recipe,
}: {
  currentId: string;
  userId: string;
  recipe: (userDetail: UserInfo) => void;
}) => {
  const queryKey = ['/user', userId, currentId];
  queryClient.setQueriesData<UserInfo | undefined>(queryKey, pre => {
    if (!pre) {
      return;
    }
    return produce(pre, draft => recipe(draft));
  });
};

export const fetchUserInfo = (id: string) => {
  const addr = String(id).startsWith('0x') ? id : `0x${id}`;
  return request.get<{ user: UserInfo }>('/user', { id: addr.toLowerCase() });
};

export const useUserInfo = (user_addr?: string, cacheTime?: number, key?: string) => {
  const { address: meId, isVerified } = useCurrentAddressInfo();
  const { putHiChatLocalUserInfo } = useHiChatLocalUserInfo();
  const { updateHiUserCache } = useHiUserCache();

  const queryKey = ['/user', user_addr, meId, isVerified];

  if (key) {
    queryKey.push(key);
  }

  return useQuery(
    queryKey,
    async () => {
      const result = await fetchUserInfo(user_addr!);
      const user = result?.user;
      if (meId === user_addr) {
        putHiChatLocalUserInfo(user.unread_message_count);
      }
      if (user_addr && user) {
        updateHiUserCache({
          [user_addr]: cloneDeep(user),
        });
      }
      return user;
    },
    {
      enabled: !!user_addr && !getIsOfficialAccountById(user_addr) && !isSystemAddress(user_addr),
      cacheTime,
      onError(err: any) {
        console.log('fetch user detail failed', user_addr, err);
      },
    },
  );
};

export const invalidUserInfo = (address: string, myId?: string) => {
  queryClient.invalidateQueries(['/user', address, myId]);
};

export const updateUserDetailQuery = (
  detail: Pick<UserInfo, 'id'> & Partial<Omit<UserInfo, 'id'>> & Partial<Omit<UserDetailRes, 'id'>>,
) => {
  const { address, isVerified } = getCurrentAddressInfo() || {};

  queryClient.setQueryData(
    ['/user', detail.id, address, isVerified],
    (old?: Partial<UserInfo>) => ({
      ...old,
      ...detail,
    }),
  );
};

// feed_draw 抽奖
// article 评论

export type UserPermissionRes = {
  details?: {
    has_web3_id: boolean;
    min_net_worth: boolean;
    min_age_days: boolean;
    min_tvf: boolean;
    min_follower_count: boolean;
    min_ranking: boolean;
    has_one_of_badges: boolean;
    is_group_member: boolean;
    has_followed_user: boolean;
  };
  has_permission: boolean;
  reason?: string;
  used_count?: number; // 用户当前操作使用次数
  total_count?: number; // 用户社交排名可使用次数，base * user_level_factor
  wait_seconds?: number; // 等待多久可以发起操作
};

type PermissionOperateType =
  | 'feed_draw'
  | 'feed_post'
  | 'feed_comment'
  // @deprecated 用 feed_comment 代替了
  | 'article'
  | 'feed_poll'
  | 'chat'
  | 'group_join';

/**
 * @deprecated
 *
 * 下线 livechat
 */
export type ChatType =
  | 'user'
  | 'official'
  | 'token'
  | 'nft_collection'
  | 'badge'
  | 'official_badge';

type PermissionOperateArgs = {
  draw_id?: number;
  article_id?: number;
  poll_id?: number;
  chat_id?: string | number;
  chain_id?: string;
  chat_type?: ChatType;
  group_id?: number;
};

const postUserPermission = async (
  operate_type: PermissionOperateType,
  operate_args?: PermissionOperateArgs,
) =>
  await request.post<UserPermissionRes>('/user/check_permission', {
    operate_type,
    operate_args,
  });
export const useUserPermission = (operate_type: PermissionOperateType) => {
  return useMutation(async (operate_args: PermissionOperateArgs) => {
    return await postUserPermission(operate_type, operate_args);
  });
};

export const useCurrentUserPermission = ({
  operate_type,
  operate_args,
  enabled = true,
}: {
  operate_type: PermissionOperateType;
  operate_args?: PermissionOperateArgs;
  enabled?: boolean;
}) => {
  const { address } = useCurrentAddressInfo();
  const isMobile = useDeviceTypeIsMobile();

  return useQuery(
    ['/user/check_permission', address, operate_type],
    async () => {
      return await postUserPermission(operate_type, operate_args);
    },
    {
      enabled: !!address && !isMobile && enabled,
    },
  );
};

export const useHasWeb3Id = () => {
  const { isVerified, userInfo, detail } = useCurrentAddressInfo();
  return useMemo(
    () => !isVerified || !(userInfo?.desc?.user?.web3_id || detail?.account_id),
    [detail?.account_id, isVerified, userInfo?.desc?.user?.web3_id],
  );
};

export const logoutUser = (credential: string) => {
  return request.get('/user/logout', undefined, { credential, hideMessage: true });
};

export const useSystemAccount = (id?: string) => {
  const url = '/system_user';
  const { userDict } = useHiUserCache();
  const info = id && (userDict[id] as SystemAccount);

  const select = useCallback((res: { user: SystemAccount }) => {
    return res.user;
  }, []);

  const isSystemAccount = useMemo(() => isSystemAddress(id), [id]);

  return useQuery(
    [url, id],
    () => {
      return request.get<{ user: SystemAccount }>(url, { id: id! });
    },
    {
      enabled: isSystemAccount,
      select,
      placeholderData: isSystemAccount && info ? { user: info } : undefined,
    },
  );
};

export const useUserBanner = (id?: string) => {
  const path = '/user/banner';
  const query = useQuery(
    [path, id],
    () => {
      return request.get<{
        banner_url: string;
        banner_thumbnail_url: string;
      }>(path, { id: id! });
    },
    {
      enabled: !!id,
    },
  );

  return query;
};

export const updateUserBannerQuery = (
  address: string,
  data: { banner_url: string; banner_thumbnail_url: string },
) => {
  queryClient.setQueriesData(['/user/banner', address], () => data);
};

const url = '/user/search_v3';

export const searchUserV3 = async (
  q: string,
  is_addr: 0 | 1 = 1,
  // 1 或者 null，默认会匹配 id ; 0，不根据 id 查询，只根据 web3id 或者机构号 name
): Promise<{
  users: {
    id: string;
    desc: UserDesc;
  }[];
}> => {
  if (!q) {
    return Promise.resolve({
      users: [],
    });
  }
  return request.get(url, { q, is_addr });
};

export const useSearchUserV3 = async (q: string, is_addr: 0 | 1 = 1) => {
  return useQuery([url, q, is_addr], () => searchUserV3(q, is_addr));
};
